/**
 * The house mapping as RULES: for each stage (and seat), which model NAMES work.
 *
 * A rule is a name or a glob, never a model row — so the same stage runs on
 * Ollama Cloud, a rented GPU or a customer's own key, whichever enabled
 * provider carries the name. The pool picks at call time. Beside each rule
 * the editor shows what it matches right now, and the modal shows the pool's
 * candidates and every recorded result for the stage, so "allowed" is a
 * choice made against evidence. Only a policy that has never been live can be
 * edited.
 */
import { useCallback, useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import Modal from '../Modal';
import type {
  BenchAdvice,
  HouseModelName,
  PoolCandidates,
  Seat,
  StageEnum,
  StageEnumOption,
  StageModelResult,
  StageModelRule,
} from '../../types';
import {
  appSummary,
  cannotRun,
  candidateSummary,
  deadRulesReason,
  matchingNames,
  seatsThatCannotRun,
  groupRules,
  groupStagesByApp,
  matchesLabel,
  narrow,
  orderStages,
  parsePatterns,
  patternMatches,
  deleteResultWarning,
  resultProviders,
  resultSource,
  ruleKey,
  rulesFor,
  rulesReplacedByPick,
  suggestedGlob,
} from '../../pipeline/houseRules';
import BenchAdvicePanel from './BenchAdvicePanel';

interface Props {
  versionId: number;
  versionName: string;
  readOnly: boolean;
  stageEnums: StageEnumOption[];
  /** Rule counts live in the version bar above; tell it when they change. */
  onChanged?: () => void;
  /** The version the admin's own calls run on (the draft they test, else the
   *  live one). */
  runsOn?: VersionRef | null;
  /** A draft to make changes in, when this version is read-only. */
  draft?: VersionRef | null;
  /** Switch the policy bar to another version. */
  onOpenVersion?: (id: number) => void;
  /** Copy a version into a new draft; resolves with the draft's id. */
  onForkToDraft?: (fromVersionId: number) => Promise<number>;
}

interface VersionRef {
  id: number;
  name: string;
}

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

export default function HouseRulesEditor({
  versionId,
  versionName,
  readOnly,
  stageEnums,
  onChanged,
  runsOn = null,
  draft = null,
  onOpenVersion,
  onForkToDraft,
}: Props) {
  const [rules, setRules] = useState<StageModelRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<StageEnumOption | null>(null);
  const [modelNames, setModelNames] = useState<HouseModelName[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRules(await api.listPipelineRules(versionId));
      onChanged?.();
    } catch (e) {
      setError(errText(e, 'Could not load the rules'));
    } finally {
      setLoading(false);
    }
    // `onChanged` reloads the bar; depending on it would reload in a circle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api
      .pipelineModelNames()
      .then(setModelNames)
      .catch(() => setModelNames([]));
  }, [versionId]);

  const grouped = useMemo(() => groupRules(rules), [rules]);
  const stages = useMemo(() => orderStages(stageEnums), [stageEnums]);
  // Every job that cannot run, the job's own chain first and then its seats.
  const brokenJobs = useMemo(
    () =>
      stages.flatMap((s) => {
        const reason = cannotRun(s, grouped);
        const seats = seatsThatCannotRun(s.value, rules);
        if (reason) return [{ stage: s, reason }];
        if (seats.length)
          return [{ stage: s, reason: `seats ${seats.map((a) => a.replace(/_/g, ' ')).join(', ')} have rules that match no model` }];
        return [];
      }),
    [stages, grouped, rules],
  );
  // Under a heading per app: the list only grows as products arrive, and the
  // server already withholds apps a non-admin does not hold.
  const appGroups = useMemo(() => groupStagesByApp(stageEnums), [stageEnums]);
  // EXPANDED rather than collapsed, so everything starts folded: thirty-seven
  // jobs across five apps is a wall, and you are here for one of them. Held
  // this way round for a second reason — the apps are not known until the job
  // list loads, so a "collapsed" set would have nothing to fill itself with.
  // Session-only on purpose: which app you care about is a property of what
  // you are doing right now, not a setting.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleApp = (product: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(product)) next.add(product);
      return next;
    });
  const allCollapsed = expanded.size === 0;

  // `?stage=X&open=1&seat=Y` — the model chips are links, so "change what the
  // Skeptic runs on" has to land ON the Skeptic rather than on a long list.
  const [searchParams, setSearchParams] = useSearchParams();
  // From a read-only dialog to the same job and seat in the draft: the URL
  // names them, and the editor for the draft opens them when it mounts.
  const goToVersion = (id: number, stageValue?: string, seat?: string | null) => {
    if (stageValue) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('stage', stageValue);
        next.set('open', '1');
        if (seat) next.set('seat', seat);
        else next.delete('seat');
        return next;
      });
    }
    onOpenVersion?.(id);
  };
  const editInDraft =
    draft && onOpenVersion
      ? (stageValue: string, seat: string | null) => goToVersion(draft.id, stageValue, seat)
      : null;
  // NO DRAFT TO GO TO: make one here rather than sending you to the bar to
  // work out which button copies what. It copies the version you are reading.
  const forkToDraft =
    !draft && onForkToDraft
      ? async (stageValue?: string, seat?: string | null) => {
          setError(null);
          try {
            goToVersion(await onForkToDraft(versionId), stageValue, seat);
          } catch (e) {
            setError(errText(e, 'Could not make a draft'));
          }
        }
      : null;
  const wantStage = searchParams.get('stage');
  const wantOpen = searchParams.get('open') === '1';
  const wantSeat = searchParams.get('seat');
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!wantStage || !wantOpen || !stages.length) return;
    if (openedFor.current === wantStage) return;
    const target = stages.find((s) => s.value === wantStage);
    if (!target) return;
    openedFor.current = wantStage;
    setEditing(target);
    // Open the app behind it too, so closing the dialog leaves the job you
    // followed the link to in view rather than a folded heading.
    setExpanded((prev) => new Set(prev).add(target.product ?? ''));
  }, [wantStage, wantOpen, stages]);

  return (
    <div data-testid="house-rules-editor">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Allowed models - {versionName}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            For each job the app hands to a model, the model names allowed to do it. Several per job
            is the point: the pool runs whichever enabled provider carries the name - ours, or the
            customer's own key. A job with no rules of its own follows its group, then DEFAULT.
            {readOnly && ' This one cannot be changed.'}
            {readOnly && forkToDraft && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => void forkToDraft()}
                  data-testid="fork-to-draft"
                  className="underline font-medium text-indigo-700 dark:text-indigo-300"
                >
                  Copy {versionName} to a new policy →
                </button>
              </>
            )}
            {readOnly && !forkToDraft && !draft && ' Copy it to a new policy to change it.'}
            {readOnly && draft && onOpenVersion && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => onOpenVersion(draft.id)}
                  className="underline font-medium text-indigo-700 dark:text-indigo-300"
                >
                  Change it in {draft.name} →
                </button>
              </>
            )}
          </p>
        </div>
        {appGroups.length > 1 && rules.length > 0 && (
          <button
            type="button"
            onClick={() =>
              setExpanded(allCollapsed ? new Set(appGroups.map((a) => a.product)) : new Set())
            }
            className="shrink-0 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* WHICH POLICY YOUR OWN CALLS RUN ON. An admin runs on the draft they
          test; reading the live policy to find out why your own calls fail is
          reading the wrong one — which is how a dead rule in a draft went
          unfound while the live policy "looked fine". */}
      {runsOn && runsOn.id !== versionId && (
        <p className="mb-3 text-sm text-amber-800 dark:text-amber-200" data-testid="runs-on-notice">
          Your own account runs on <span className="font-medium">{runsOn.name}</span>, not {versionName}.{' '}
          {onOpenVersion && (
            <button type="button" onClick={() => onOpenVersion(runsOn.id)} className="underline">
              Show {runsOn.name}
            </button>
          )}
        </p>
      )}

      {!loading && rules.length > 0 && brokenJobs.length > 0 && (
        <div
          data-testid="cannot-run-banner"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200"
        >
          <p className="font-medium">
            {brokenJobs.length} job{brokenJobs.length === 1 ? '' : 's'} can't run on {versionName}
            {readOnly ? '' : ' as it stands'}:
          </p>
          <ul className="mt-1 space-y-0.5">
            {brokenJobs.slice(0, 6).map(({ stage: s, reason }) => (
              <li key={s.value}>
                <button type="button" onClick={() => setEditing(s)} className="underline">
                  {s.label}
                </button>{' '}
 - {reason}
              </li>
            ))}
            {brokenJobs.length > 6 && <li>…and {brokenJobs.length - 6} more below.</li>}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
      ) : rules.length === 0 ? (
        /* One message, not thirty red rows: an empty version has nothing per
           stage to say. Live and empty is an outage; a draft is unfinished. */
        <div
          data-testid="house-rules-empty"
          className={`rounded-lg border px-4 py-5 text-sm ${
            readOnly
              ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
              : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100'
          }`}
        >
          <p className="font-medium">
            {readOnly
              ? 'This policy allows no models. Every customer call fails until a policy with rules is made live.'
              : 'This policy allows no models yet.'}
          </p>
          <p className="mt-1">
            Start with a rule on DEFAULT - every job without one of its own follows it - or paste a
            policy copied from another server with{' '}
            <span className="font-medium">+ Paste a policy</span> in the bar above
            {readOnly ? ', then make it live.' : '.'}
          </p>
          {!readOnly && stages.some((s) => s.value === 'DEFAULT') && (
            <button
              type="button"
              onClick={() => setEditing(stages.find((s) => s.value === 'DEFAULT')!)}
              className="mt-3 px-3 py-1.5 bg-blue-600 text-white rounded text-sm"
            >
              Allow a model for DEFAULT
            </button>
          )}
        </div>
      ) : (
        appGroups.map((app) => {
        const open = expanded.has(app.product);
        return (
        <div key={app.product} className="mb-5" data-testid={`app-group-${app.product || 'shared'}`}>
          <button
            type="button"
            onClick={() => toggleApp(app.product)}
            aria-expanded={open}
            className="mb-1.5 w-full flex items-baseline gap-2 text-left"
          >
            <span className="text-gray-400 dark:text-gray-500 text-[10px]" aria-hidden>
              {open ? '▼' : '▶'}
            </span>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {app.label}
            </span>
            {/* Collapsed, the count alone says nothing about whether it is
                worth opening; what is SET here does. */}
            <span className="text-xs text-gray-400">
              {open
                ? `${app.stages.length} job${app.stages.length === 1 ? '' : 's'}`
                : appSummary(app.stages, grouped)}
            </span>
          </button>
        {open && (
        <div className="border rounded-lg divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
          {app.stages.map((s) => {
            const own = grouped.get(ruleKey(s.value, null)) ?? [];
            const effective = rulesFor(s, grouped);
            const seatRules = rules.filter((r) => r.stage === s.value && r.archetype);
            return (
              <div key={s.value} className="px-4 py-3 flex items-start justify-between gap-4" data-testid={`rule-row-${s.value}`}>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{s.label}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wide text-gray-400">{s.value}</span>
                    {s.is_group && (
                      <span className="text-[10px] uppercase tracking-wide text-indigo-600 dark:text-indigo-300">group</span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                    {own.length === 0 && effective.from && !effective.own && (
                      <span className="text-gray-500 dark:text-gray-400">
                        follows <span className="font-medium">{effective.fromLabel}</span> ·
                      </span>
                    )}
                    {cannotRun(s, grouped) && (
                      <span className="text-red-600 dark:text-red-400" data-testid={`cannot-run-${s.value}`}>
                        {cannotRun(s, grouped)}
                        {effective.from ? ' ·' : ''}
                      </span>
                    )}
                    {(own.length ? own : effective.rules).map((r) => (
                      <span
                        key={r.id}
                        title={matchesLabel(r) || undefined}
                        className={`font-mono px-1.5 py-0.5 rounded border ${
                          r.enabled
                            ? r.matches.length
                              ? 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100'
                              : 'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300'
                            : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-400 line-through'
                        }`}
                      >
                        {r.model_pattern}
                        {r.matches.length > 1 && <span className="opacity-60"> ×{r.matches.length}</span>}
                      </span>
                    ))}
                    {seatRules.length > 0 && (
                      <span className="text-gray-500 dark:text-gray-400">
                        · {new Set(seatRules.map((r) => r.archetype)).size} seat
                        {new Set(seatRules.map((r) => r.archetype)).size === 1 ? '' : 's'} pinned
                      </span>
                    )}
                    {seatsThatCannotRun(s.value, rules).length > 0 && (
                      <span className="text-red-600 dark:text-red-400">
                        · can't run: {seatsThatCannotRun(s.value, rules).map((a) => a.replace(/_/g, ' ')).join(', ')} (rules match no model)
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(s)}
                  className="shrink-0 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {readOnly ? 'View' : 'Edit'}
                </button>
              </div>
            );
          })}
        </div>
        )}
        </div>
        );
        })
      )}

      {editing && (
        <StageRulesModal
          versionId={versionId}
          stage={editing}
          rules={rules.filter((r) => r.stage === editing.value)}
          readOnly={readOnly}
          versionName={versionName}
          draftName={draft?.name ?? null}
          runsOnName={runsOn && runsOn.id !== versionId ? runsOn.name : null}
          onEditInDraft={editInDraft}
          onForkToDraft={forkToDraft}
          inheritedReason={cannotRun(editing, grouped)}
          inherited={(() => {
            const eff = rulesFor(editing, grouped);
            return eff.own || !eff.fromLabel ? null : { label: eff.fromLabel, rules: eff.rules };
          })()}
          modelNames={modelNames}
          initialSeat={editing.value === wantStage ? wantSeat : null}
          onClose={() => setEditing(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

interface ModalProps {
  versionId: number;
  stage: StageEnumOption;
  rules: StageModelRule[];
  readOnly: boolean;
  versionName: string;
  /** The draft a read-only dialog can send you to, and how to get there. */
  draftName: string | null;
  onEditInDraft: ((stageValue: string, seat: string | null) => void) | null;
  /** No draft to go to: copy this version into one, landing on the same job. */
  onForkToDraft: ((stageValue?: string, seat?: string | null) => Promise<void>) | null;
  /** Set when the admin's own calls run on a different version than this. */
  runsOnName: string | null;
  /** Why the job cannot run on the rules it inherits (group, DEFAULT), or null. */
  inheritedReason: string | null;
  /** The rules this job runs on when it has none of its own, and their rung. */
  inherited: { label: string; rules: StageModelRule[] } | null;
  modelNames: HouseModelName[];
  /** Seat tab to open on, from a `?seat=` deep link. */
  initialSeat?: string | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
}


function StageRulesModal({
  versionId,
  stage,
  rules,
  readOnly,
  versionName,
  draftName,
  onEditInDraft,
  onForkToDraft,
  runsOnName,
  inheritedReason,
  inherited,
  modelNames,
  initialSeat = null,
  onClose,
  onChanged,
}: ModalProps) {
  const [seats, setSeats] = useState<Seat[]>([]);
  const [seat, setSeat] = useState<string | null>(initialSeat); // null = every seat
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<PoolCandidates | null>(null);
  const [results, setResults] = useState<StageModelResult[]>([]);
  const [advice, setAdvice] = useState<BenchAdvice | null>(null);
  const [resultForm, setResultForm] = useState({ model_name: '', metric: '', score: '', sample_size: '', notes: '' });

  const hasSeats = (stage.seat_count ?? 0) > 0;
  useEffect(() => {
    if (!hasSeats) return;
    api
      .listStageSeats(stage.value as StageEnum)
      .then((r) => setSeats(r.items))
      .catch(() => setSeats([]));
  }, [hasSeats, stage.value]);

  const refreshSide = useCallback(async () => {
    try {
      const [c, r, a] = await Promise.all([
        api.pipelineCandidates(versionId, stage.value, seat),
        api.pipelineResults(stage.value, seat),
        // The suggestion is advice, not the editor: if it fails, edit without it.
        api.pipelineAdvice(versionId, stage.value, seat).catch(() => null),
      ]);
      setCandidates(c);
      setResults(r);
      setAdvice(a);
    } catch (e) {
      setError(errText(e, 'Could not load candidates'));
    }
  }, [versionId, stage.value, seat]);

  useEffect(() => {
    void refreshSide();
  }, [refreshSide]);

  const here = rules.filter((r) => (r.archetype ?? null) === seat);
  // What runs when this tab has no rules: a seat falls back to the every-seat
  // rules of its own job first, and only then down the job's chain.
  const seatless = rules.filter((r) => !r.archetype && r.enabled);
  const applied =
    seat && seatless.length
      ? { label: 'the every-seat rules', rules: seatless }
      : inherited && inherited.rules.length
        ? inherited
        : null;
  // The picker: everything, narrowed by what is typed. `patternHits` is whether
  // the text AS A RULE (exact or glob) would match anything — plain "glm" does
  // not, so the Allow button waits and a `*glm*` glob is offered instead.
  const shown = narrow(modelNames, input);
  const patternHits = modelNames.some((m) => patternMatches(input, m.model_name));
  // Matching a model the POOL cannot pick is worth allowing (the rule starts
  // working the moment its provider is shared) but must be said out loud.
  const poolHits = modelNames.some((m) => m.in_pool !== false && patternMatches(input, m.model_name));
  const blocked = modelNames.filter((m) => m.in_pool === false && patternMatches(input, m.model_name));
  const glob = suggestedGlob(input);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
      await refreshSide();
    } catch (e) {
      setError(errText(e, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };

  // Sampling edits live in the list until blur, so typing "0.15" does not fire
  // three saves (and does not lose the "0." on the way).
  const [sampling, setSamplingState] = useState<Record<number, { temperature?: string; top_p?: string }>>({});
  const setSampling = (id: number, patch: { temperature?: string; top_p?: string }) =>
    setSamplingState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  // A pattern being edited in the list. Saving is add-then-remove, in that
  // order, so the job is never left with no rule at all.
  const [editingPattern, setEditingPattern] = useState<{ id: number; text: string } | null>(null);
  const savePattern = (r: StageModelRule) => {
    const next = (editingPattern?.text ?? '').trim().toLowerCase();
    setEditingPattern(null);
    if (!next || next === r.model_pattern) return;
    void run(async () => {
      await api.putPipelineRule(versionId, {
        stage: r.stage,
        archetype: r.archetype,
        model_pattern: next,
        enabled: r.enabled,
        temperature: r.temperature,
        top_p: r.top_p,
      });
      await api.deletePipelineRule(versionId, r.id);
    });
  };

  const saveSampling = (r: StageModelRule) => {
    const edit = sampling[r.id];
    if (!edit) return;
    const num = (raw: string | undefined, current: number | null) => {
      if (raw === undefined) return current;
      const t = raw.trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : current;
    };
    const next = {
      temperature: num(edit.temperature, r.temperature),
      top_p: num(edit.top_p, r.top_p),
    };
    setSamplingState((s) => {
      const { [r.id]: _gone, ...rest } = s;
      return rest;
    });
    if (next.temperature === r.temperature && next.top_p === r.top_p) return;
    void run(() =>
      api.putPipelineRule(versionId, {
        stage: r.stage,
        archetype: r.archetype,
        model_pattern: r.model_pattern,
        enabled: r.enabled,
        ...next,
      }),
    );
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    const patterns = parsePatterns(input);
    if (!patterns.length) return;
    void run(async () => {
      for (const p of patterns) {
        await api.putPipelineRule(versionId, { stage: stage.value, archetype: seat, model_pattern: p });
      }
      setInput('');
    });
  };

  const addResult = (e: FormEvent) => {
    e.preventDefault();
    const score = Number(resultForm.score);
    if (!resultForm.model_name.trim() || !resultForm.metric.trim() || Number.isNaN(score)) return;
    void run(async () => {
      await api.addPipelineResult({
        stage: stage.value,
        archetype: seat,
        model_name: resultForm.model_name.trim(),
        metric: resultForm.metric.trim(),
        score,
        sample_size: resultForm.sample_size ? Number(resultForm.sample_size) : null,
        notes: resultForm.notes || null,
      });
      setResultForm({ model_name: '', metric: '', score: '', sample_size: '', notes: '' });
    });
  };

  return (
    <Modal isOpen onClose={onClose} title={`${stage.label} - allowed models`} size="lg">
      <div className="space-y-4">
        {hasSeats && (
          <div className="flex flex-wrap gap-1.5 text-xs">
            <SeatTab active={seat === null} onClick={() => setSeat(null)} label="Every seat" />
            {seats.map((s) => (
              <SeatTab key={s.archetype} active={seat === s.archetype} onClick={() => setSeat(s.archetype)} label={s.label} />
            ))}
          </div>
        )}
        {seat && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Rules here apply to the {seats.find((s) => s.archetype === seat)?.label ?? seat} seat only, and
            replace the every-seat rules for it.
          </p>
        )}

        {/* WHY THERE IS NO INPUT. Read-only used to mean the allow form and
            the Remove buttons simply were not there, with the reason on the
            page behind the dialog. */}
        {readOnly && (
          <div
            data-testid="read-only-notice"
            className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-100"
          >
            <span className="font-medium">{versionName}</span> is read-only.{' '}
            {onEditInDraft && draftName ? (
              <button type="button" onClick={() => onEditInDraft(stage.value, seat)} className="underline font-medium">
                Edit this in draft {draftName} →
              </button>
            ) : onForkToDraft ? (
              <button
                type="button"
                onClick={() => void onForkToDraft(stage.value, seat)}
                className="underline font-medium"
              >
                Copy {versionName} into a draft and edit it here →
              </button>
            ) : (
              'Make a draft in the policy bar to change it.'
            )}
            {runsOnName && (
              <p className="mt-1">
                Your own account runs on <span className="font-medium">{runsOnName}</span>, not this version.
              </p>
            )}
          </div>
        )}

        {/* Its own rules when it has any; otherwise what it inherits — a seat
            with no rules follows the job, and the job follows DEFAULT. */}
        {(here.some((r) => r.enabled) ? deadRulesReason(here) : inheritedReason) && (
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="dead-rules-warning">
            {seat ? 'This seat' : 'This job'}{' '}
            {here.some((r) => r.enabled) ? deadRulesReason(here) : inheritedReason}, so every call fails.
            {readOnly ? '' : ' Allow a model that exists from the list below.'}
          </p>
        )}

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <BenchAdvicePanel
          advice={advice}
          readOnly={readOnly}
          busy={busy}
          inheritedFrom={here.some((r) => r.enabled) ? null : (applied?.label ?? null)}
          onAllow={(model) =>
            run(() => api.putPipelineRule(versionId, { stage: stage.value, archetype: seat, model_pattern: model }))
          }
          onUseOnly={(model) =>
            run(async () => {
              // Allow first, then remove: never a moment with no rule on the job.
              await api.putPipelineRule(versionId, { stage: stage.value, archetype: seat, model_pattern: model });
              for (const r of rulesReplacedByPick(here, model)) {
                await api.deletePipelineRule(versionId, r.id);
              }
            })
          }
          rules={here}
          onRemove={(ruleIds) =>
            run(async () => {
              for (const id of ruleIds) {
                await api.deletePipelineRule(versionId, id);
              }
            })
          }
        />

        {/* WHICH RULES ACTUALLY APPLY. "No rules for this seat — it follows
            DEFAULT" left you to work out what DEFAULT allows from the pool box
            underneath, so the models in force were never on screen. They are
            now, each with a way to pin it here. */}
        <p className="text-xs font-medium text-gray-600 dark:text-gray-300" data-testid="applied-from">
          {here.length > 0 ? (
            <>In force {seat ? 'for this seat' : 'for this job'}: its own rules.</>
          ) : applied ? (
            <>
              In force {seat ? 'for this seat' : 'for this job'}, from{' '}
              <span className="font-semibold">{applied.label}</span> - it has none of its own:
            </>
          ) : (
            <>Nothing is in force: no rule here, on its group, or on DEFAULT.</>
          )}
        </p>

        {here.length === 0 && applied && (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-dashed rounded-md" data-testid="inherited-list">
            {applied.rules.map((r) => (
              <li key={r.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <span className={`font-mono ${r.matches.length ? 'text-gray-600 dark:text-gray-300' : 'text-red-600 dark:text-red-400'}`}>
                    {r.model_pattern}
                  </span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    {matchesLabel(r) || 'matches 1 model'} · from {applied.label}
                  </span>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(() =>
                        api.putPipelineRule(versionId, {
                          stage: stage.value,
                          archetype: seat,
                          model_pattern: r.model_pattern,
                          temperature: r.temperature,
                          top_p: r.top_p,
                        }),
                      )
                    }
                    title={`Copy this rule onto ${seat ? 'this seat' : 'this job'}, so a change to ${applied.label} cannot take it away`}
                    className="shrink-0 px-2 py-0.5 border border-blue-600 text-blue-700 dark:text-blue-300 rounded text-[11px] disabled:opacity-50"
                  >
                    Allow here
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <ul className="divide-y divide-gray-200 dark:divide-gray-700 border rounded-md" data-testid="rules-list">
          {here.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              No rules {seat ? 'for this seat' : 'of its own'} - it follows{' '}
              {seat ? 'the every-seat rules, then ' : ''}
              {stage.fallback?.map((f) => f.label).join(', then ') || 'nothing'}.
            </li>
          )}
          {here.map((r) => (
            <li key={r.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
              <div className="min-w-0">
                {/* EDIT THE PATTERN IN PLACE. Widening gemma4:31b to
                    gemma4:31* — so it takes the name with and without a -cloud
                    suffix — meant removing the rule and typing a new one, with
                    nothing to show what the new pattern would catch until after
                    it was saved. The preview below answers that while typing. */}
                {editingPattern?.id === r.id ? (
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      savePattern(r);
                    }}
                  >
                    <input
                      autoFocus
                      value={editingPattern.text}
                      onChange={(e) => setEditingPattern({ id: r.id, text: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setEditingPattern(null);
                      }}
                      onBlur={() => savePattern(r)}
                      data-testid="pattern-input"
                      className="w-56 font-mono text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-0.5"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {matchingNames(editingPattern.text, modelNames).join(', ') || 'matches nothing'}
                    </span>
                  </form>
                ) : (
                  <>
                    <button
                      type="button"
                      disabled={readOnly || busy}
                      onClick={() => setEditingPattern({ id: r.id, text: r.model_pattern })}
                      title={readOnly ? undefined : 'Edit this pattern - a * takes in every name that starts the same way'}
                      className={`font-mono text-left ${r.enabled ? '' : 'line-through text-gray-400'} ${
                        readOnly ? '' : 'hover:underline decoration-dotted'
                      }`}
                    >
                      {r.model_pattern}
                    </button>
                    <span
                      className={`ml-2 text-xs ${
                        r.enabled && !r.matches.length
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {matchesLabel(r)}
                    </span>
                  </>
                )}
                {r.notes && <p className="text-xs text-gray-500 dark:text-gray-400">{r.notes}</p>}
                {/* HOW to run it, when the model's default is wrong for this
                    job: extraction wants a cold model, a room a warm one.
                    Blank leaves the model's own default alone. */}
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <label className="flex items-center gap-1">
                    temp
                    <input
                      value={sampling[r.id]?.temperature ?? (r.temperature ?? '')}
                      disabled={readOnly || busy}
                      placeholder="default"
                      inputMode="decimal"
                      onChange={(e) => setSampling(r.id, { temperature: e.target.value })}
                      onBlur={() => saveSampling(r)}
                      className="w-16 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5"
                    />
                  </label>
                  <label className="flex items-center gap-1">
                    top_p
                    <input
                      value={sampling[r.id]?.top_p ?? (r.top_p ?? '')}
                      disabled={readOnly || busy}
                      placeholder="default"
                      inputMode="decimal"
                      onChange={(e) => setSampling(r.id, { top_p: e.target.value })}
                      onBlur={() => saveSampling(r)}
                      className="w-16 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5"
                    />
                  </label>
                </div>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-3 shrink-0 text-xs">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(() =>
                        api.putPipelineRule(versionId, {
                          stage: r.stage,
                          archetype: r.archetype,
                          model_pattern: r.model_pattern,
                          enabled: !r.enabled,
                        }),
                      )
                    }
                    className="text-gray-600 dark:text-gray-300 hover:underline"
                  >
                    {r.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => run(() => api.deletePipelineRule(versionId, r.id))}
                    className="text-red-700 dark:text-red-300 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {!readOnly && (
          <form onSubmit={add} className="space-y-2" data-testid="allow-form">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="type to narrow - glm, gemma4:*, claude-opus-4-8"
                className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
              />
              <button
                type="submit"
                disabled={busy || !parsePatterns(input).length || !patternHits}
                title={patternHits ? undefined : 'This exact pattern matches no model; pick one below or allow a glob.'}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
              >
                Allow {input.trim() ? <span className="font-mono">{input.trim()}</span> : ''}
              </button>
              {glob && shown.length > 0 && !patternHits && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(async () => {
                    await api.putPipelineRule(versionId, { stage: stage.value, archetype: seat, model_pattern: glob });
                    setInput('');
                  })}
                  className="px-3 py-1.5 border border-blue-600 text-blue-700 dark:text-blue-300 rounded text-sm"
                  title={`Allow every model matching ${glob} (${shown.length} right now)`}
                >
                  Allow <span className="font-mono">{glob}</span>
                </button>
              )}
            </div>
            {/* A model that matches but sits on a personal, paused or keyless
                provider is allowable and simply will not run yet. Saying so
                beats the rule quietly matching nothing. */}
            {patternHits && !poolHits && blocked.length > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="not-in-pool-warning">
                {blocked[0].model_name} exists on {blocked[0].other_providers?.join(', ') || 'a provider'}, which the
                pool cannot use. Allow it if you like - it starts running once that provider is shared and enabled
                under Providers.
              </p>
            )}
            {/* Every model you can see, narrowed as you type. Click one to
                allow exactly that name. */}
            <ul
              data-testid="model-picker"
              className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 border rounded-md text-xs"
            >
              {shown.length === 0 && (
                <li className="px-3 py-2 text-gray-500 dark:text-gray-400">
                  {modelNames.length === 0 ? 'No models registered yet.' : 'No model has that in its name.'}
                </li>
              )}
              {shown.map((m) => {
                const allowed = here.some((r) => r.enabled && patternMatches(r.model_pattern, m.model_name));
                const usable = m.in_pool !== false;
                return (
                  <li key={m.model_name}>
                    <button
                      type="button"
                      onClick={() => setInput(m.model_name)}
                      className="w-full text-left px-3 py-1.5 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span className={`font-mono ${usable ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}>
                        {m.model_name}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400 truncate">
                        {usable ? m.providers.join(', ') : m.other_providers?.join(', ')}
                        {allowed && <span className="ml-2 text-emerald-700 dark:text-emerald-300">allowed</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </form>
        )}

        <div className="rounded-md bg-gray-50 dark:bg-gray-900/40 p-3 text-xs">
          <p className="font-medium text-gray-700 dark:text-gray-200">
            Pool right now: {candidateSummary(candidates)}
            {candidates?.from_stage && candidates.from_stage !== stage.value && (
              <span className="font-normal text-gray-500"> (rules from {candidates.from_stage})</span>
            )}
          </p>
          {candidates && candidates.candidates.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-gray-600 dark:text-gray-300">
              {candidates.candidates.map((c) => (
                <li key={`${c.llm_settings_id}-${c.provider_id}`} className="font-mono">
                  {c.model_name} <span className="opacity-70">on {c.provider_name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* The raw evidence behind the suggestion at the top, collapsed: it is
            for checking or removing a result, not for choosing a model. */}
        <details className="rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2" data-testid="recorded-results">
          <summary className="cursor-pointer text-sm font-medium text-gray-800 dark:text-gray-100">
            Recorded results{seat ? ' for this seat' : ''} ({results.length})
            <span className="ml-2 font-normal text-xs text-gray-500 dark:text-gray-400">
              the evidence the suggestion above is built from
            </span>
          </summary>
          {results.length === 0 ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Nothing recorded yet.</p>
          ) : (
            <table className="mt-2 w-full text-xs">
              <thead className="text-gray-500 dark:text-gray-400 text-left">
                <tr>
                  <th className="font-normal py-1">model</th>
                  <th className="font-normal py-1">provider</th>
                  <th className="font-normal py-1">metric</th>
                  <th className="font-normal py-1 text-right">score</th>
                  <th className="font-normal py-1 text-right">n</th>
                  <th className="font-normal py-1 pl-2">source</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const providers = resultProviders(r);
                  const source = resultSource(r);
                  return (
                    <tr key={r.id} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="py-1 font-mono">{r.model_name}</td>
                      <td className={`py-1 ${providers.length > 1 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-gray-300'}`}>
                        {providers.length ? providers.join(', ') : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="py-1">{r.metric}</td>
                      <td className="py-1 text-right font-mono">{r.score}</td>
                      <td className="py-1 text-right">{r.sample_size ?? ''}</td>
                      <td className="py-1 pl-2" title={r.notes ?? undefined}>
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] ${
                            source === 'bench'
                              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                              : source === 'imported seat bench'
                                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {source}
                        </span>
                        <span className="ml-1 text-gray-500">{r.recorded_at ? r.recorded_at.slice(0, 10) : ''}</span>
                      </td>
                      <td className="py-1 text-right">
                        <button
                          type="button"
                          disabled={busy}
                          title="Delete this result"
                          onClick={() => {
                            const warning = deleteResultWarning(r);
                            if (warning && !window.confirm(warning)) return;
                            void run(() => api.deletePipelineResult(r.id));
                          }}
                          className="icon-btn text-red-700 dark:text-red-300 hover:underline"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <form onSubmit={addResult} className="mt-2 grid grid-cols-6 gap-1.5 text-xs">
            <input
              list="house-model-names"
              value={resultForm.model_name}
              onChange={(e) => setResultForm({ ...resultForm, model_name: e.target.value })}
              placeholder="model"
              className="col-span-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 font-mono"
            />
            <datalist id="house-model-names">
              {modelNames.map((m) => (
                <option key={m.model_name} value={m.model_name} />
              ))}
            </datalist>
            <input
              value={resultForm.metric}
              onChange={(e) => setResultForm({ ...resultForm, metric: e.target.value })}
              placeholder="metric"
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1"
            />
            <input
              value={resultForm.score}
              onChange={(e) => setResultForm({ ...resultForm, score: e.target.value })}
              placeholder="score"
              inputMode="decimal"
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1"
            />
            <input
              value={resultForm.sample_size}
              onChange={(e) => setResultForm({ ...resultForm, sample_size: e.target.value })}
              placeholder="n"
              inputMode="numeric"
              className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1"
            />
            <button type="submit" disabled={busy} className="px-2 py-1 bg-gray-700 text-white rounded disabled:opacity-50">
              Record
            </button>
          </form>
        </details>

        {/* Every change here saves as you make it, so the way out is just out. */}
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SeatTab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded border ${
        active
          ? 'bg-indigo-600 text-white border-indigo-600'
          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
      }`}
    >
      {label}
    </button>
  );
}
